/**
 * A failed turn in the transcript (TMMAPP-0142 item 4), as catalog copy:
 * - a wallet reason (`WALLET_EMPTY`, `AI_DEBT {debtMinor}`, the first purchase, and the wallet's
 *   other refusals) is `AiWalletPrompt` in place (DLG-005);
 * - the gate's other reasons (`FEATURE`, `GLOBAL`, `USER`, …) read the existing AI copy ("This isn't
 *   your setting" for the service's own switches);
 * - the turn's own reasons read their sentence; `INTERRUPTED` and the retryable ones offer Try again,
 *   which re-sends the person's message.
 * - too large for one question (TMMAPI-0182 U1, J7): `CONTEXT_WINDOW`, and `ATTACHMENT_TOO_LARGE`
 *   whose `limitKind` is `CONTEXT_WINDOW`, read one sentence with no number in it; an
 *   `ATTACHMENT_TOO_LARGE` over a byte limit keeps its own.
 * - the payer's refusal (TMMAPP-0148 U1 item 8; TMMAPI-0184 U3 `PAYER_UNAVAILABLE`): "The assistant
 *   isn't available here right now: {payer}'s AI credit can't cover it." No figure, no wallet prompt
 *   and no top-up, whatever `failureDetails.reason` says: the person who asked doesn't pay. The
 *   reason picks the sentence (TMMAPI-0185 U1): `MONTHLY_LIMIT`, `BUSY` (with Try again) and
 *   `UNAVAILABLE` (our side) have their own; any other keeps item 8's.
 * - the member's own allowance used up (TMMAPP-0153 item 17; TMMAPI-0186 U1 `MEMBER_LIMIT`): "You've
 *   reached your monthly allowance for the assistant on {virtual account}. {owner} can change it."
 *   No figure, no wallet prompt and no Try again.
 */
import { Alert, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MEMBER_LIMIT, PAYER_UNAVAILABLE, type AssistantTurn } from '@/api/assistant';
import { aiDisabledCopy } from '@/api/errorCopy';
import { isAiWalletRefusal, type AiRefusal } from '@/api/errors';
import { AiWalletPrompt } from '@/features/aiWallet/AiWalletPrompt';
import { memberLimitCopy, payerUnavailableCopy, turnReasonCopy } from './assistantCopy';

const RETRYABLE: ReadonlySet<string> = new Set(['INTERRUPTED', 'PROVIDER_ERROR', 'LOOP_LIMIT']);
/** Reasons the gate shares with `409 AI_DISABLED` that have their own sentence. */
const GATE_REASONS: ReadonlySet<string> = new Set([
  'GLOBAL',
  'FEATURE',
  'USER',
  'DAILY_CAP',
  'SUSPENDED',
]);

interface TurnFailureProps {
  turn: AssistantTurn;
  /** Re-sends the message; absent when it can't be found. */
  onTryAgain?: (() => void) | undefined;
  /** The session's payer's name (`paidBy`), for `PAYER_UNAVAILABLE`; null when not served. */
  payerName?: string | null | undefined;
  /** `MEMBER_LIMIT`'s sentence, with the session's virtual account and owner (`memberLimitCopy`). */
  memberLimitText?: string | undefined;
}

export function TurnFailure({
  turn,
  onTryAgain,
  payerName = null,
  memberLimitText,
}: TurnFailureProps) {
  const { t } = useTranslation();
  const served = turn.failureReason ?? '';
  if (served === MEMBER_LIMIT) {
    return (
      <Alert severity="warning" data-turn-failure={served} data-member-limit="">
        {memberLimitText ?? memberLimitCopy(t, null, null)}
      </Alert>
    );
  }
  if (served === PAYER_UNAVAILABLE) {
    // BUSY is the payer's calls in flight: "try again in a moment" offers Try again.
    const busy = turn.payerReason === 'BUSY';
    return (
      <Alert
        severity="warning"
        data-turn-failure={served}
        action={
          busy && onTryAgain !== undefined ? (
            <Button color="inherit" size="small" onClick={onTryAgain}>
              {t('aiWallet.tryAgain')}
            </Button>
          ) : null
        }
      >
        {payerUnavailableCopy(t, payerName, turn.payerReason)}
      </Alert>
    );
  }
  const reason =
    served === 'ATTACHMENT_TOO_LARGE' && turn.failureLimitKind === 'CONTEXT_WINDOW'
      ? 'CONTEXT_WINDOW'
      : served;
  const refusal: AiRefusal = {
    reason: turn.failureReason,
    availableMinor: turn.availableMinor,
    firstPurchaseRequired: turn.firstPurchaseRequired,
    debtMinor: turn.debtMinor,
  };
  if (isAiWalletRefusal(refusal)) {
    return (
      <AiWalletPrompt
        refusal={refusal}
        meterDebt={false}
        onTryAgain={onTryAgain ?? (() => undefined)}
      />
    );
  }
  const own = turnReasonCopy(t, reason);
  const text =
    own ?? (GATE_REASONS.has(reason) ? aiDisabledCopy(t, refusal) : t('assistant.failure.unknown'));
  const retry = own === null ? !GATE_REASONS.has(reason) : RETRYABLE.has(reason);
  return (
    <Alert
      severity="warning"
      data-turn-failure={reason}
      action={
        retry && onTryAgain !== undefined ? (
          <Button color="inherit" size="small" onClick={onTryAgain}>
            {t('aiWallet.tryAgain')}
          </Button>
        ) : null
      }
    >
      {text}
    </Alert>
  );
}
